/**
 * featureFlags.js
 * ===============
 * Toggle features by setting the DEFAULT below OR by flipping them at
 * runtime via the in-app Feature Flags panel (Idenza logo → ⋯ menu →
 * Feature flags in the sidebar footer).
 *
 * How it works:
 *   - Each flag has a `default` (the value shipped in this file).
 *   - `getFlag` reads localStorage first, falls back to the default.
 *   - When a user toggles a flag in the UI, we write to localStorage and
 *     reload the tab so imports pick up the new value.
 *
 * Usage in components (unchanged):
 *   import { isPortfolioDashboardV2 } from "../featureFlags";
 */

const STORAGE_PREFIX = "idz.flag.";

/** Read a flag from localStorage, falling back to `defaultValue`. */
const getFlag = (name, defaultValue) => {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + name);
    if (raw === null) return defaultValue;
    return raw === "true";
  } catch {
    return defaultValue;
  }
};

/** Persist a flag override. Call `reloadForFlags()` afterwards to apply. */
export const setFlag = (name, value) => {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + name, value ? "true" : "false");
  } catch {
    /* localStorage disabled — silently ignore */
  }
};

/** Wipe a single flag override (revert to the default in this file). */
export const resetFlag = (name) => {
  try {
    window.localStorage.removeItem(STORAGE_PREFIX + name);
  } catch { /* ignore */ }
};

/** Wipe all flag overrides. */
export const resetAllFlags = () => {
  try {
    Object.keys(window.localStorage)
      .filter((k) => k.startsWith(STORAGE_PREFIX))
      .forEach((k) => window.localStorage.removeItem(k));
  } catch { /* ignore */ }
};

/** Reload the tab so `import` statements pick up the new values. */
export const reloadForFlags = () => {
  window.location.reload();
};

// ─── Flag registry — the source of truth for the UI panel ─────────────────
// Add every new flag here so the Feature Flags panel can render it.
// `label` and `description` show in the UI; keep them short and specific.
export const FLAG_REGISTRY = [
  {
    name: "isSSBAMLEnabled",
    label: "SSB AML dashboard",
    description: "AML Execution Results dashboard for SSB Bank.",
    default: true,
  },
  {
    name: "isNewDashboard",
    label: "New Deposit Outcome dashboard",
    description: "Off = legacy Risk & Payments Command Center.",
    default: true,
  },
  {
    name: "isPortfolioDashboardV2",
    label: "Portfolio dashboard v2 (facelift)",
    description: "Off = pre-facelift demo version of the sponsor-bank portfolio dashboard (what you shipped).",
    default: false,
  },
  {
    name: "isSidebarV2",
    label: "Sidebar v2 (facelift)",
    description: "Off = original cobalt sidebar from the demo.",
    default: false,
  },
  {
    name: "showCaseManagementLink",
    label: "Show Case Management link",
    description: "Show the Case Management entry in the External section.",
    default: true,
  },
  {
    name: "enableCaseManagementLink",
    label: "Enable Case Management link",
    description: "Off = show the link as an Add-on chip (non-clickable).",
    default: true,
  },
  {
    name: "isSidebarBlue",
    label: "Sidebar blue (was teal)",
    description: "Off = senior's teal sidebar (default). On = cobalt blue sidebar matching the launch banner.",
    default: false,
  },
  {
    name: "isIsoDashboard",
    label: "ISO portfolio dashboard",
    description: "External ISO/reseller demo surface (senior's v2 base). Derived-fixture, 4-eyes agent action, rule what-if, consequence ledger, vertical config.",
    default: true,
  },
  {
    name: "isTitanReportingEnabled",
    label: "Titan Treasure reporting",
    description: "Summary, user investigation, cards and decision rates for the Titan Treasure realm (DS-238).",
    default: true,
  },
];

// ─── Resolved flag values ─────────────────────────────────────────────────
// These are the frozen values the app sees at page-load time. Toggling in
// the UI updates localStorage; a page reload re-runs these lines and picks
// up the new values.
export const isSSBAMLEnabled          = getFlag("isSSBAMLEnabled",          true);
export const isNewDashboard           = getFlag("isNewDashboard",           true);
// Facelift work is opt-in — defaults to false so the app renders the
// demo state exactly as it shipped. Flip the flag in the UI (or here) to
// preview the new work.
export const isPortfolioDashboardV2   = getFlag("isPortfolioDashboardV2",   false);
export const isSidebarV2              = getFlag("isSidebarV2",              false);
export const showCaseManagementLink    = getFlag("showCaseManagementLink",    true);
export const enableCaseManagementLink  = getFlag("enableCaseManagementLink",  true);
export const enableStablecoinDashboard = true;
export const isSidebarBlue             = getFlag("isSidebarBlue",             false);
export const isIsoDashboard            = getFlag("isIsoDashboard",            true);
export const isTitanReportingEnabled   = getFlag("isTitanReportingEnabled",   true);
